import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Paper,
    Alert,
    CircularProgress
} from '@mui/material';
import { MarkEmailRead, ArrowBack } from '@mui/icons-material';
import { FddApi } from '../../ui/services/server-fdd';
import logo from '../../assets/webp/logo_complete.webp';
import logoWhite from '../../assets/webp/logo_complete_white.webp';
import { useThemeMode } from '../../ui/context/theme/ThemeProvider';

export const ForgotPasswordPage = () => {
    const { mode } = useThemeMode();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email.trim() || !emailRegex.test(email)) {
            setError('Ingresa un correo electrónico válido');
            return;
        }

        setLoading(true);
        try {
            const result = await FddApi.forgotPassword(email.trim().toLowerCase());
            if (result.hasError) {
                setError(result.message || 'Error al enviar el correo');
            } else {
                setSent(true);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default' }}>
            <Container maxWidth="xs">
                <Paper elevation={3} sx={{ p: { xs: 5, sm: 6 }, borderRadius: 2, mx: 'auto', maxWidth: 420 }}>
                    <Box
                        component="img"
                        src={mode === 'dark' ? logoWhite : logo}
                        alt="Neftis SecureSign"
                        sx={{ width: '70%', maxWidth: 280, mb: 4, display: 'block', mx: 'auto' }}
                    />

                    {sent ? (
                        /* ── Estado: correo enviado ── */
                        <Box sx={{ textAlign: 'center' }}>
                            <MarkEmailRead sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                            <Typography variant="h5" fontWeight="600" gutterBottom>
                                Revisa tu correo
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Si <strong>{email}</strong> está registrado, recibirás un enlace para restablecer tu contraseña en los próximos minutos.
                            </Typography>
                            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                                El enlace expira en <strong>1 hora</strong>. Revisa también tu carpeta de spam.
                            </Alert>
                            <Button component={Link} to="/login" variant="outlined" startIcon={<ArrowBack />} fullWidth>
                                Volver al inicio de sesión
                            </Button>
                        </Box>
                    ) : (
                        /* ── Formulario ── */
                        <>
                            <Typography component="h1" variant="h5" fontWeight="600" gutterBottom>
                                Recuperar contraseña
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.
                            </Typography>

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                            )}

                            <Box component="form" onSubmit={handleSubmit}>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Correo Electrónico"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    autoFocus
                                />
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    sx={{ mt: 2, mb: 2, py: 1.2 }}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} /> : null}
                                    {loading ? 'Enviando...' : 'Enviar enlace de recuperación'}
                                </Button>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Link to="/login" style={{ color: '#1976d2', textDecoration: 'none', fontSize: '0.875rem' }}>
                                        ← Volver al inicio de sesión
                                    </Link>
                                </Box>
                            </Box>
                        </>
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

export default ForgotPasswordPage;
